import React from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, Grid, Paper, Stack, Alert, LinearProgress, Fade,
  List, ListItem, ListItemIcon, ListItemText,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import SpeedIcon from '@mui/icons-material/Speed';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import HotelIcon from '@mui/icons-material/Hotel';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import SectionHeader from './SectionHeader';
import DetailSection from './DetailSection';
import { GradientBackground } from './styled';

const SessionQualityDetail = ({ aiAnalysis, legacyInsights, timeMetrics, legacyDuration, onBack }) => {
  const theme = useTheme();
  const insights = aiAnalysis?.insights || [];
  const qualityScore = aiAnalysis?.session_quality_score;
  const encouragement = aiAnalysis?.encouragement;
  
  const duration = timeMetrics?.elapsed_duration_seconds 
    ? Math.round(timeMetrics.elapsed_duration_seconds / 60) 
    : legacyDuration;
    
  const activeTime = timeMetrics?.active_duration_seconds 
    ? Math.round(timeMetrics.active_duration_seconds / 60) 
    : null;
    
  const restTime = timeMetrics?.total_rest_seconds 
    ? Math.round(timeMetrics.total_rest_seconds / 60) 
    : null;
    
  const efficiency = timeMetrics?.efficiency_percent;

  const categoryIcons = {
    performance: <SpeedIcon sx={{ fontSize: 18 }} />,
    technique: <FitnessCenterIcon sx={{ fontSize: 18 }} />,
    recovery: <HotelIcon sx={{ fontSize: 18 }} />,
    progress: <TrendingUpIcon sx={{ fontSize: 18 }} />,
    motivation: <WhatshotIcon sx={{ fontSize: 18 }} />,
    warning: <WarningAmberIcon sx={{ fontSize: 18 }} />,
  };

  const priorityColors = { 1: 'error', 2: 'warning', 3: 'info' };

  return (
    <Fade in timeout={300}>
      <Box>
        <SectionHeader icon={<SpeedIcon />} title="Session Quality" onBack={onBack} />
        
        {/* Score Card */}
        <GradientBackground 
          color={qualityScore >= 80 ? 'success' : qualityScore >= 60 ? 'primary' : qualityScore > 0 ? 'warning' : 'info'} 
          sx={{ p: 3, mb: 3 }}
        >
          <Grid container spacing={3}>
            {qualityScore > 0 && (
              <Grid item xs={6} sm={4}>
                <Typography variant="overline" color="text.secondary">Quality Score</Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <Typography variant="h2" fontWeight={800} color={qualityScore >= 80 ? 'success.main' : qualityScore >= 60 ? 'primary.main' : 'warning.main'}>
                    {qualityScore.toFixed(0)}<Typography component="span" variant="h5" color="text.secondary">/100</Typography>
                  </Typography>
                </Box>
              </Grid>
            )}
            {duration && (
              <Grid item xs={6} sm={4}>
                <Typography variant="overline" color="text.secondary">Duration</Typography>
                <Typography variant={qualityScore > 0 ? 'h3' : 'h2'} fontWeight={qualityScore > 0 ? 700 : 800} color={qualityScore > 0 ? 'text.primary' : 'info.main'}>
                  {duration}
                  <Typography component="span" variant="body1" color="text.secondary" sx={{ ml: 0.5 }}>min</Typography>
                </Typography>
              </Grid>
            )}
            {efficiency && efficiency <= 100 && (
              <Grid item xs={6} sm={4}>
                <Typography variant="overline" color="text.secondary">Efficiency</Typography>
                <Typography variant="h3" fontWeight={700}>
                  {efficiency.toFixed(0)}
                  <Typography component="span" variant="body1" color="text.secondary" sx={{ ml: 0.5 }}>%</Typography>
                </Typography>
              </Grid>
            )}
          </Grid>
          
          {qualityScore > 0 && (
            <LinearProgress 
              variant="determinate" 
              value={qualityScore} 
              color={qualityScore >= 80 ? 'success' : qualityScore >= 60 ? 'primary' : 'warning'}
              sx={{ mt: 2, height: 8, borderRadius: 1 }}
            />
          )}
        </GradientBackground>

        {encouragement && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{encouragement}</Typography>
          </Alert>
        )}

        {/* Time Breakdown */}
        {(activeTime || restTime > 0) && (
          <DetailSection title="Time Breakdown" icon={<AccessTimeIcon sx={{ fontSize: 18 }} />} color="info">
            <Grid container spacing={2}>
              {activeTime && (
                <Grid item xs={6} sm={4}>
                  <Box sx={{ 
                    p: 1.5, 
                    bgcolor: alpha(theme.palette.success.main, 0.08), 
                    borderRadius: 2,
                    textAlign: 'center',
                  }}>
                    <Typography variant="h4" fontWeight={700} color="success.main">{activeTime}</Typography>
                    <Typography variant="caption" color="text.secondary">Active (min)</Typography>
                  </Box>
                </Grid>
              )}
              {restTime > 0 && (
                <Grid item xs={6} sm={4}>
                  <Box sx={{ 
                    p: 1.5, 
                    bgcolor: alpha(theme.palette.info.main, 0.08), 
                    borderRadius: 2,
                    textAlign: 'center',
                  }}>
                    <Typography variant="h4" fontWeight={700}>{restTime}</Typography>
                    <Typography variant="caption" color="text.secondary">Rest (min)</Typography>
                  </Box>
                </Grid>
              )}
              {timeMetrics?.work_rest_ratio > 0 && (
                <Grid item xs={6} sm={4}>
                  <Box sx={{ 
                    p: 1.5, 
                    bgcolor: alpha(theme.palette.primary.main, 0.08), 
                    borderRadius: 2,
                    textAlign: 'center',
                  }}>
                    <Typography variant="h4" fontWeight={700}>1:{(1 / timeMetrics.work_rest_ratio).toFixed(1)}</Typography>
                    <Typography variant="caption" color="text.secondary">Work:Rest</Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </DetailSection>
        )}

        {/* Insights */}
        {insights.length > 0 ? (
          <DetailSection title="Assistant Insights" icon={<LightbulbOutlinedIcon sx={{ fontSize: 18 }} />} color="primary">
            <Stack spacing={2}>
              {insights.map((insight, idx) => (
                <Paper 
                  key={idx} 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    borderRadius: 2,
                    borderLeft: `4px solid ${theme.palette[priorityColors[insight.priority] || 'info'].main}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Box sx={{ color: `${priorityColors[insight.priority] || 'info'}.main`, mt: 0.25 }}>
                      {categoryIcons[insight.category] || <InfoOutlinedIcon sx={{ fontSize: 18 }} />}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>{insight.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{insight.content}</Typography>
                      {insight.actionable && insight.action_suggestion && (
                        <Typography variant="body2" color="primary.main" sx={{ mt: 1 }}>
                          💡 {insight.action_suggestion}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Stack>
          </DetailSection>
        ) : legacyInsights?.length > 0 ? (
          <DetailSection title="Assistant Insights" icon={<LightbulbOutlinedIcon sx={{ fontSize: 18 }} />} color="primary">
            <List disablePadding>
              {legacyInsights.map((insight, idx) => (
                <ListItem key={idx} sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <LightbulbOutlinedIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={insight} />
                </ListItem>
              ))}
            </List>
          </DetailSection>
        ) : null}
      </Box>
    </Fade>
  );
};

SessionQualityDetail.propTypes = {
  aiAnalysis: PropTypes.object,
  legacyInsights: PropTypes.array,
  timeMetrics: PropTypes.object,
  legacyDuration: PropTypes.number,
  onBack: PropTypes.func.isRequired,
};

export default SessionQualityDetail;